import { FormEvent, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Ban, Check, X } from 'lucide-react'
import { Alerta, AreaTexto, Badge, Boton, Campo, Cargando, Dialogo, Pestanas } from '../../components/kit'
import { useAuth } from '../../contexts/AuthProvider'
import { useAnularSolicitud, useAprobarSolicitud, useRechazarSolicitud, useSolicitudes } from '../../queries/useInventario'
import { SolicitudTraslado } from '../../types/inventario'
import { mensajeDeError } from '../../utils/errores'
import { formatoFecha } from '../../utils/fecha'
import { ESTADO_SOLICITUD } from '../../utils/inventario'

/**
 * Las solicitudes de traslado. Quien administra bienes las ve todas y aprueba (los
 * bienes se trasladan en ese momento) o rechaza con una respuesta; quien las pide ve
 * las suyas y puede anularlas mientras están pendientes.
 */
export default function Traslados() {
  const { puede } = useAuth()
  const admin = puede('administra_bienes')
  const [pestana, setPestana] = useState<'PENDIENTE' | 'RESUELTAS'>('PENDIENTE')
  const solicitudes = useSolicitudes(pestana)
  const [rechazando, setRechazando] = useState<SolicitudTraslado | null>(null)

  return (
    <div className="pila pila--amplia">
      <header className="pagina__cabecera">
        <h1 className="dcc-t-heading-m">Traslados</h1>
        <p className="texto-secundario">
          {admin
            ? 'Lo que se pidió trasladar. Al aprobar, los bienes se trasladan y queda en el historial; al rechazar, se explica por qué.'
            : 'Los traslados que pediste. Se piden desde la ficha de un bien o marcando varios en una oficina.'}
        </p>
      </header>

      <Pestanas
        etiqueta="Solicitudes"
        activa={pestana}
        onCambiar={(c) => setPestana(c as 'PENDIENTE' | 'RESUELTAS')}
        pestanas={[
          { clave: 'PENDIENTE', texto: 'Pendientes' },
          { clave: 'RESUELTAS', texto: 'Resueltas' },
        ]}
      >
        {solicitudes.isLoading && <Cargando />}
        {solicitudes.isError && <Alerta tipo="error">{mensajeDeError(solicitudes.error)}</Alerta>}
        {solicitudes.data && !solicitudes.data.length && (
          <p className="texto-secundario">{pestana === 'PENDIENTE' ? 'No hay solicitudes pendientes.' : 'Todavía no hay solicitudes resueltas.'}</p>
        )}
        <ul className="solicitudes">
          {solicitudes.data?.map((s) => (
            <TarjetaSolicitud key={s.id} solicitud={s} admin={admin} onRechazar={() => setRechazando(s)} />
          ))}
        </ul>
      </Pestanas>

      {rechazando && <DialogoRechazar solicitud={rechazando} onCerrar={() => setRechazando(null)} />}
    </div>
  )
}

function TarjetaSolicitud({ solicitud: s, admin, onRechazar }: { solicitud: SolicitudTraslado; admin: boolean; onRechazar: () => void }) {
  const aprobar = useAprobarSolicitud()
  const anular = useAnularSolicitud()
  const estado = ESTADO_SOLICITUD[s.estado]
  const pendiente = s.estado === 'PENDIENTE'
  const error = aprobar.error ?? anular.error

  return (
    <li className="solicitud">
      <div className="solicitud__cabecera">
        <Badge tono={estado.tono} punto>{estado.texto}</Badge>
        <span className="solicitud__destino">
          A <Link to={`/app/oficinas/${s.destino.codigo}`}>{s.destino.nombre}</Link> <span className="texto-secundario">({s.destino.codigo})</span>
        </span>
        <span className="texto-secundario solicitud__fecha">{s.solicitante} · {formatoFecha(s.fecha_creacion)}</span>
      </div>
      <p className="solicitud__motivo">«{s.motivo}»</p>
      <ul className="solicitud__bienes">
        {s.items.map(({ bien, origen, actual }) => (
          <li key={bien.id}>
            <Link to={`/app/bienes/${bien.id}`}>{bien.numero || 'Sin N°'}</Link> {bien.descripcion}
            <span className="texto-secundario">
              {' '}· {origen?.nombre ?? 'sin oficina'} <ArrowRight className="dcc-i" size={12} aria-hidden /> {s.destino.nombre}
            </span>
            {pendiente && actual?.codigo !== origen?.codigo && <Badge tono="error">Ya no está en {origen?.nombre ?? 'su oficina'}</Badge>}
          </li>
        ))}
      </ul>
      {!pendiente && (
        <p className="texto-secundario">
          {s.estado === 'ANULADA' ? 'Anulada por quien la pidió' : `${estado.texto} por ${s.resuelta_por}`} · {formatoFecha(s.fecha_resolucion)}
          {s.respuesta && <> · «{s.respuesta}»</>}
        </p>
      )}
      {pendiente && !s.vigente && (
        <Alerta tipo="error">Algún bien cambió de lugar desde que se pidió: no se puede aprobar. Recházala; si hace falta, que se pida de nuevo.</Alerta>
      )}
      {!!error && <Alerta tipo="error">{mensajeDeError(error)}</Alerta>}
      {pendiente && (admin || s.propia) && (
        <div className="solicitud__acciones">
          {s.propia && <Boton tam="sm" variante="text" icono={Ban} cargando={anular.isPending} onClick={() => anular.mutate(s.id)}>Anular</Boton>}
          {admin && (
            <>
              <Boton tam="sm" variante="outlined" color="error" icono={X} onClick={onRechazar}>Rechazar</Boton>
              <Boton tam="sm" color="success" icono={Check} cargando={aprobar.isPending} disabled={!s.vigente} onClick={() => aprobar.mutate({ id: s.id })}>
                Aprobar y trasladar
              </Boton>
            </>
          )}
        </div>
      )}
    </li>
  )
}

function DialogoRechazar({ solicitud, onCerrar }: { solicitud: SolicitudTraslado; onCerrar: () => void }) {
  const rechazar = useRechazarSolicitud()
  const [respuesta, setRespuesta] = useState('')
  const guardar = (e: FormEvent) => {
    e.preventDefault()
    rechazar.mutate({ id: solicitud.id, respuesta }, { onSuccess: onCerrar })
  }
  return (
    <Dialogo abierto onCerrar={onCerrar} titulo="Rechazar el traslado">
      <form className="pila" onSubmit={guardar}>
        <p className="texto-secundario">Los bienes se quedan donde están. {solicitud.solicitante} verá tu respuesta en Traslados.</p>
        <Campo etiqueta="Por qué se rechaza" requerido>
          <AreaTexto rows={3} value={respuesta} onChange={(e) => setRespuesta(e.target.value)} required />
        </Campo>
        {rechazar.isError && <Alerta tipo="error">{mensajeDeError(rechazar.error)}</Alerta>}
        <div className="acciones-formulario">
          <Boton variante="text" onClick={onCerrar}>Cancelar</Boton>
          <Boton type="submit" color="error" cargando={rechazar.isPending} disabled={!respuesta.trim()}>Rechazar</Boton>
        </div>
      </form>
    </Dialogo>
  )
}
